import { connectWebSocket } from '../services/websocket'
import type { RealtimeRepairReason } from './useRealtimeRepair'

export interface RealtimeRepairScheduler {
  /** Call on every event for the watched resource; resets the stall timer. */
  notifyActivity: () => void
  /** Detach listeners and cancel the timer. Safe to call more than once. */
  stop: () => void
}

export interface CreateRealtimeRepairSchedulerOptions {
  onRepair: (reason: RealtimeRepairReason) => void
  stallTimeoutMs?: number
  reconnectGapMs?: number
}

const DEFAULT_STALL_TIMEOUT_MS = 30_000
const DEFAULT_RECONNECT_GAP_MS = 2_000
const MAX_STALL_BACKOFF_MULTIPLIER = 8

/**
 * Imperative twin of `useRealtimeRepair`, for await-style flows that are not
 * React components and so cannot hold hook state.
 *
 * Same contract: a repair means "you may have missed an event, re-subscribe and
 * take the server's snapshot". Callers are expected to make `onRepair`
 * idempotent, because a false stall is deliberately preferred over a hung await.
 *
 * Deliberately has no visibility trigger — these flows are awaited by a caller
 * that is already mounted in a component with its own visibility handling, and
 * duplicating it here would double every repair.
 */
export function createRealtimeRepairScheduler({
  onRepair,
  stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
  reconnectGapMs = DEFAULT_RECONNECT_GAP_MS,
}: CreateRealtimeRepairSchedulerOptions): RealtimeRepairScheduler {
  const socket = connectWebSocket()
  let timer: ReturnType<typeof setTimeout> | undefined
  let attempts = 0
  let disconnectedAt: number | null = null
  let stopped = false

  const clear = () => {
    if (timer !== undefined) {
      clearTimeout(timer)
      timer = undefined
    }
  }

  const arm = () => {
    clear()
    if (stopped) {
      return
    }
    const multiplier = Math.min(2 ** attempts, MAX_STALL_BACKOFF_MULTIPLIER)
    timer = setTimeout(() => {
      timer = undefined
      attempts += 1
      onRepair('stall_timeout')
      arm()
    }, stallTimeoutMs * multiplier)
  }

  const handleDisconnect = () => {
    disconnectedAt = Date.now()
    // The stall timer keeps running: a repair reads over HTTP, which works
    // while the socket is down, and is then the only way anything arrives.
  }

  const handleConnect = () => {
    const wasDownSince = disconnectedAt
    disconnectedAt = null
    if (wasDownSince !== null && Date.now() - wasDownSince >= reconnectGapMs) {
      onRepair('reconnect_gap')
    }
    arm()
  }

  socket.on('connect', handleConnect)
  socket.on('disconnect', handleDisconnect)
  arm()

  return {
    notifyActivity: () => {
      attempts = 0
      arm()
    },
    stop: () => {
      stopped = true
      clear()
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
    },
  }
}
